import { UzButton, UzInput, UzSwitch, UzTextarea } from '@utazzo/ui';
import { Wand2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { MediaDropField } from '../../components/MediaDropField';

import { useSlugAvailability } from './use-slug-availability';

import type { UpdateWebsitePageRequest, WebsitePageDetail } from '@utazzo/shared';

interface SeoPanelProps {
  page: WebsitePageDetail;
  onSave: (payload: Partial<UpdateWebsitePageRequest>) => void;
  /**
   * Path this page type is served under (e.g. "/tourism"). Empty / omitted for
   * the types served straight off the root, where the slug IS the whole path.
   * Only used to show the author the URL they are editing.
   */
  publicPathPrefix?: string;
  disabled?: boolean;
}

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

/**
 * Free text -> kebab-case slug.
 *
 * A trailing dash survives when the author's last keystroke was a dash or a
 * space: the field is controlled, so stripping it would glue the next word
 * onto the previous one ("bali " -> "bali" -> "balibeaches").
 */
const slugify = (raw: string): string => {
  const body = raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+/u, '')
    .replace(/-+$/u, '');
  return body && /[\s-]$/u.test(raw) ? `${body}-` : body;
};

/** What actually gets saved: the typed value minus any dash still dangling. */
const canonicalSlug = (s: string): string => s.replace(/-+$/u, '');

/**
 * Per-page SEO metadata editor: the URL slug, search title/description/keywords,
 * social share image (Open Graph), and the indexing/sitemap switches. Text
 * fields persist debounced; the OG image and toggles persist immediately. All
 * fields map 1:1 to WebsitePage columns via UpdateWebsitePageRequest.
 *
 * The slug is editable here, not only at create time: a title-derived slug
 * repeats the page type in the URL ("/best-places-to-visit-in-thailand"), and
 * the author owns the final path.
 */
export const SeoPanel = ({
  page,
  onSave,
  publicPathPrefix,
  disabled,
}: SeoPanelProps): React.JSX.Element => {
  const [slug, setSlug] = useState(page.slug);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [metaTitle, setMetaTitle] = useState(page.metaTitle ?? '');
  const [metaDescription, setMetaDescription] = useState(page.metaDescription ?? '');
  const [metaKeywords, setMetaKeywords] = useState(page.metaKeywords ?? '');

  // Re-sync only when navigating to a DIFFERENT page. Syncing on every field
  // change would clobber a field still being typed each time a sibling field's
  // debounced save lands and the parent re-renders with the merged page.
  useEffect(() => {
    setSlug(page.slug);
    setSlugError(null);
    setMetaTitle(page.metaTitle ?? '');
    setMetaDescription(page.metaDescription ?? '');
    setMetaKeywords(page.metaKeywords ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- page.id only: see above.
  }, [page.id]);

  // One timer per field, not one shared timer: editing two fields inside the
  // same debounce window must save both, not just the last one touched.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const saveDebounced = (key: string, payload: Partial<UpdateWebsitePageRequest>): void => {
    clearTimeout(timers.current.get(key));
    timers.current.set(
      key,
      setTimeout(() => onSave(payload), 600),
    );
  };

  // The slug save fires from an effect once the availability check clears, so
  // it reads the handler through a ref rather than re-running on its identity.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  /** Apply a slug edit: validate and show it. Saving waits on the check below. */
  const applySlug = (raw: string): void => {
    // Shown before sanitising so the author sees the character we're objecting
    // to rather than a value that silently lost it.
    if (raw.includes('/')) {
      setSlug(raw);
      setSlugError('Use one path segment - a slug with "/" is not served');
      return;
    }

    const next = slugify(raw);
    setSlug(next);

    const value = canonicalSlug(next);
    if (!value) {
      setSlugError('Enter a URL slug');
      return;
    }

    setSlugError(null);
    // The save is not queued here - it waits for the availability check below,
    // so a slug another page already answers is never sent.
  };

  // What a save would write, or null when there's nothing to write: unchanged,
  // empty, or already rejected on format.
  const pendingSlug = slugError ? null : canonicalSlug(slug) || null;
  const slugToCheck = pendingSlug && pendingSlug !== page.slug ? pendingSlug : null;
  const availability = useSlugAvailability(page.pageType, slugToCheck, page.id);

  // Save once the server says the slug is free. The check is itself debounced,
  // so this fires on a pause in typing rather than per keystroke - and a taken
  // slug simply never reaches the save.
  useEffect(() => {
    if (!slugToCheck || availability.status !== 'free') return;
    onSaveRef.current({ slug: slugToCheck });
  }, [slugToCheck, availability.status]);

  /** Leaving an unusable slug behind would show a URL the page doesn't have. */
  const revertSlugIfInvalid = (): void => {
    if (!slugError) {
      setSlug(canonicalSlug(slug));
      return;
    }
    setSlug(page.slug);
    setSlugError(null);
  };

  const publicUrl = `${publicPathPrefix ?? ''}/${slug}`;
  // The server's own words for what holds this URL - it knows which page.
  const takenError = availability.status === 'taken' ? availability.reason : undefined;

  return (
    <div className="flex flex-col gap-5 px-4 pt-2 pb-6">
      <div>
        <label className="mb-1 block text-xs font-medium text-(--fg-muted)">URL slug</label>
        <div className="flex items-start gap-2">
          <UzInput
            containerClassName="flex-1"
            value={slug}
            placeholder="bali"
            disabled={disabled}
            error={slugError ?? takenError}
            onChange={(e) => applySlug(e.target.value)}
            onBlur={revertSlugIfInvalid}
          />
          <UzButton
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-label="Generate slug from title"
            title="Generate slug from title"
            onClick={() => applySlug(page.title)}
          >
            <Wand2 className="h-4 w-4" />
          </UzButton>
        </div>

        {/* A taken slug is the everyday case now that both destination guides
            are served off the root: `/bali` can only be one of them. Say which
            page holds it and offer the free alternative, so the author fixes it
            here instead of finding out from a failed save. */}
        {!slugError && availability.status === 'taken' && availability.suggestion && (
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-(--fg-muted)">
            <span>Try</span>
            <button
              type="button"
              disabled={disabled}
              className="font-medium text-(--primary) hover:underline disabled:opacity-50"
              onClick={() => applySlug(availability.suggestion!)}
            >
              /{availability.suggestion}
            </button>
            <span>- it&rsquo;s free.</span>
          </p>
        )}
        {!slugError && (
          <p className="mt-1 text-[12px] text-(--fg-faint)">Public URL: {publicUrl}</p>
        )}
        <p className="mt-1 text-[12px] text-(--fg-faint)">
          Lowercase letters, digits and dashes. Changing this changes the page&rsquo;s live URL -
          the old one stops working. Two pages cannot share a URL, so a destination&rsquo;s guides
          need one slug each.
        </p>
      </div>

      <Field label="Meta title" hint="Shown in the browser tab and search results. ~60 characters.">
        <UzInput
          value={metaTitle}
          placeholder={page.title}
          disabled={disabled}
          onChange={(e) => {
            setMetaTitle(e.target.value);
            saveDebounced('metaTitle', { metaTitle: e.target.value || null });
          }}
        />
      </Field>

      <Field
        label="Meta description"
        hint="The snippet under the title in search results. ~155 characters."
      >
        <UzTextarea
          value={metaDescription}
          rows={3}
          disabled={disabled}
          onChange={(e) => {
            setMetaDescription(e.target.value);
            saveDebounced('metaDescription', { metaDescription: e.target.value || null });
          }}
        />
      </Field>

      <Field label="Keywords" hint="Comma-separated. Optional - most engines ignore these.">
        <UzInput
          value={metaKeywords}
          placeholder="travel, holidays, india"
          disabled={disabled}
          onChange={(e) => {
            setMetaKeywords(e.target.value);
            saveDebounced('metaKeywords', { metaKeywords: e.target.value || null });
          }}
        />
      </Field>

      <Field
        label="Social share image (Open Graph)"
        hint="Recommended 1200×630. Used when the page is shared on social media."
      >
        <MediaDropField
          value={page.ogImageId}
          kind="image"
          disabled={disabled}
          onChange={(id) => onSave({ ogImageId: id })}
        />
      </Field>

      <div className="flex flex-col gap-3 rounded-lg border border-(--border) bg-(--z-50) p-3.5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-(--fg)">Show in sitemap</div>
            <div className="text-[12px] text-(--fg-faint)">
              Include this page in the generated sitemap.xml.
            </div>
          </div>
          <UzSwitch
            checked={page.showInSitemap}
            disabled={disabled}
            onCheckedChange={(v) => onSave({ showInSitemap: v })}
            aria-label="Show in sitemap"
          />
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-(--border) pt-3">
          <div>
            <div className="text-sm font-medium text-(--fg)">Hide from search engines</div>
            <div className="text-[12px] text-(--fg-faint)">
              Adds a noindex tag so this page is not indexed.
            </div>
          </div>
          <UzSwitch
            checked={page.noIndex}
            disabled={disabled}
            onCheckedChange={(v) => onSave({ noIndex: v })}
            aria-label="Hide from search engines"
          />
        </div>
      </div>
    </div>
  );
};
